<template>
  <div class="row">
    <div class="col-12 col-md-4 col-lg-4">
      <div class="card">
        <div class="card-header flex-wrap pt-6 pb-0">
          <div class="card-title">
            <div class="card-label">
              <div class="d-flex">
                <i class="fas fa-folder me-3 text-primary" style="font-size: 20px;"></i>
                <h1 class="card-label fs-2">{{ $t('projects.directories') }}</h1>
              </div>
            </div>
          </div>
          <div class="card-toolbar">
            <Button v-if="$can(abilities.FOLDER_PROJECT_CREATE)" type="new" btn-class="btn-sm" iconClass="fas fa-plus" @click="openDirectoryModal"></Button>
          </div>
        </div>
        <div class="card-body">
          <div v-if="directories.length" v-for="directory in directories" :key="directory.id" class="card card-body shadow-sm mb-3" @click="showActiveDirectory(directory.id)" :class="activeDirectory === directory.id ? 'active' : ''">
            <div class="d-flex justify-content-between align-items-center">
              <h2 :class="activeDirectory === directory.id ? 'text-primary' : ''">{{ directory.name }}</h2>
              <i v-if="$can(abilities.FOLDER_PROJECT_DELETE)"  class="fas fa-trash fs-2" style="color: #989EA7;" @click.stop="deleteDirectory(directory.id)"></i>
            </div>
          </div>
          <div v-else>
            <div class="h-350px d-flex justify-content-center align-items-center" >
              <div class="text-center ">
                <div>
                  <Img filename="empty.png" alt=""
                        class="mw-100 h-125px h-sm-175px" />
                </div>
                <h2 class="fs-2x fw-bolder mb-0">{{ $t('projects.noData') }}</h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-md-8 col-lg-8">
      <div class="card">
        <div class="card-header flex-wrap pt-6 pb-0">
          <div class="card-title">
            <div class="card-label">
              <h3 class="card-label fs-2">{{ $t('projects.directory_title') }}</h3>
            </div>
          </div>
          <div class="card-toolbar" v-if="activeDirectory">
            <Button type="new" btn-class="btn-sm me-2" btn-text="projects.add_files" @click="openFileModal"></Button>
          </div>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div v-if="(showAll || files.length > 0)">
              <Button type="new" btn-class="btn-sm me-2" iconClass="fas fa-user-shield" @click="giveCustomerAccess" size="10px 25px"></Button>
              <Button type="new" btn-class="btn-sm me-2" iconClass="fas fa-download" @click="downloadAllFiles" size="10px 25px"></Button>
              <Button type="new" btn-class="btn-sm" iconClass="fas fa-trash" @click="deleteAllFiles" size="10px 25px"></Button>
            </div>
          </div>
          <div class="row">
            <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="kt_files_datatables">
              <thead>
                <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                  <th class="min-w-100px d-flex justify-content-start align-items-center">
                    <input type="checkbox" v-model="showAll" id="check_all"/>
                    <span class="ms-5">{{ $t('projects.file_name') }}</span>
                  </th>
                  <th class="min-w-100px text-center">{{ $t('projects.download_by') }}</th>
                  <th class="min-w-100px text-center">{{ $t('projects.date') }}</th>
                  <th class="min-w-100px text-center">{{ $t('projects.keywords') }} </th>
                  <th class="min-w-100px text-center">{{ $t('projects.can_be_view') }} </th>
                  <th class="min-w-100px text-center">{{ $t('projects.actions') }}</th>
                </tr>
              </thead>
            </table>
          </div>
        </div>
      </div>
    </div>

    <DirectoryModal id="directory_modal_create" />
    <FileModal id="file_modal_create" @load="fetchDatas" :folder="activeDirectory"/>
    <FilesAttachModal id="file_attach" :files="fileUrl"/>
    <UpdateFileModal id="update_file_modal_create" @load="fetchDatas" :form="formUpdate"/>
  </div>
</template>

<script setup> 
import {onMounted, ref, computed, watch} from "vue";
import {useStore} from "vuex";
import {useLocales} from "@/composables/locales";
import Button from "@/components/Button";
import {useRoute} from 'vue-router';
import DirectoryModal from "./modals/DirectoryModal"
import FileModal from "./modals/FileModal"
import {useSwal} from "@/composables/useSwal";
import { useI18n } from 'vue-i18n'
import {BASE_URL} from "@/services/api";
import {Theme} from "@/composables/theming";
import Ls from "@/services/ls";
import abilities from "@/composables/abilities";
import Img from "@/components/Img.vue";
import UpdateFileModal from "./modals/UpdateFileModal"
import FilesAttachModal from "@/components/FilesAttachModal.vue";
import {$dateFormatter, getFileIcon} from "@/mixins/Helpers";

const store = useStore()
const route = useRoute()
const {showSwal} = useSwal()
const { t } = useI18n({ useScope: 'global' })
const {locale, datatableLocaleUrl} = useLocales()

const showAll = ref(false)
const activeDirectory = ref('')
const files = ref([])
const table = ref(null)
const fileUrl = ref([])
const formUpdate = ref({
  file_id: '',
  name: '',
  key_word: [],
  customer_can_view: false
})

const directories = computed(() => store.getters['projects/getProjectDirectories'](route.params.id))

watch(() => directories.value, (value) => {
    let firstDirectory = Object.values(directories.value)[0];
    activeDirectory.value = firstDirectory?.id
})

watch(() => locale.value, (value) => {
  if(activeDirectory.value){
    fetchDatas()
  }
}, { deep: true })

watch(() => [activeDirectory.value, route.query.tab], (value) => {
  if(activeDirectory.value){
    fetchDatas()
  }
}, { deep: true })

watch(() => showAll.value, (newValue) => {
  $("#kt_files_datatables").on("draw.dt", () => {
    $("#kt_files_datatables .check_btn").prop("checked", showAll.value);

    if(showAll.value) {
      let ids = [];
      $("#kt_files_datatables .check_btn").each(function() {
        let id = $(this).attr("data-id");

        if (id) {
          ids.push(id);
        }
      });

      files.value = ids;
    } else {
      files.value= []
    }

  });
});

function showActiveDirectory(id) {
  activeDirectory.value = id
}

function loadDirectories() {
  store.dispatch('projects/loadProjectDirectories', route.params.id)
}

onMounted(() => {
  loadDirectories()
  if(activeDirectory.value){
    fetchDatas()
  }
})

function openDirectoryModal(){
  $('#directory_modal_create').modal('toggle')
}

function openFileModal(){
  $('#file_modal_create').modal('toggle')
}

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#kt_files_datatables").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'desc']],
    ajax: {
      url: BASE_URL + `/get/projectFolder/${activeDirectory.value}/files/datatables?project_folder_id=${activeDirectory.value}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
    { data: "file" }, { data: "user" }, { data: "created_at" }, { data: "key_word" },{ data: "key_word" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [
      {
        targets: -6,
        order: "desc",
        className: "dt-start text-start clickable" ,
        render: (t, a, e, l) => {
          let result = ''
          let fileIcon = getFileIcon(t, {
            type: 'i',
            size: "2.4em",
            className: "me-2"
          })
          result += '<input type="checkbox" class="check_btn" data-id="'+ e.id +'" />'
          result += '<span class="ms-5" data-id="'+ e.id +'">' + fileIcon + t + "</span>";
          return result
        },
      },
      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + $dateFormatter(t, locale.value) + "</span>";
        },
      },
      
      {
        targets: -3,
        orderable: false,
        className: "dt-center text-center" ,
        render: function (t, a, e, l) {
          let result = ''
          let keyword = (t==[]) ? JSON.parse(t) : t
          if (Array.isArray(keyword) && keyword.length) {
            for(let element of t) {
              result += '<span class="badge badge-light-primary" style="margin-right: 5px;">' + element + '</span>';
            }
          }
          return result
        },
      },
      {
        targets: -2,
        orderable: false,
        className: "dt-center text-center" ,
        render: function (t, a, e, l) {
          return `
            <label class="form-check form-switch form-check-custom form-check-solid">
              <input 
                class="form-check-input toggle-customer-view" 
                type="checkbox" 
                data-id="${e.id}" 
                ${e.customer_can_view ? 'checked="checked"' : ""} 
              />
            </label>
          `;
        },
      },
      {
        width: "15%",
        targets: -1,
        "className": "dt-center text-start",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render = ""

          render+='<a href="javascript:;" data-id="'+ e.id +'"  data-can="'+ e.customer_can_view +'" data-keywords="'+ e.key_word +'" data-file="'+ e.file +'" class="btn btn-sm btn-clean btn-icon add_btn" title="'+ i18n.global.t('button.add_keywords') +'"><i  data-id="'+ e.id +'" data-keywords="'+ e.key_word +'" data-file="'+ e.file +'" class="fas fa-plus fs-2" style="color: '+Theme.gray +'"></i></a>'
          render+='<a href="javascript:;" data-id="'+ e.id +'" data-can="'+ e.customer_can_view +'" data-file_url="'+ e.file_url +'" data-file="'+ e.file +'" class="btn btn-sm btn-clean btn-icon view_btn" title="'+ i18n.global.t('button.view') +'"><i  data-id="'+ e.id +'" data-file-url="'+ e.file_url +'" data-file="'+ e.file +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
          render+='<a href="javascript:;" data-id="'+ e.id +'" data-file_url="'+ e.file_url +'" data-file="'+ e.file +'"  class="btn btn-sm btn-clean btn-icon download_btn" title="'+ i18n.global.t('button.download') +'"><i  data-id="'+ e.id +'" data-file_url="'+ e.file_url +'" data-file="'+ e.file +'" class="fas fa-download fs-2" style="color: '+Theme.gray +'"></i></a>'
          render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i  data-id="'+ e.id +'" class="fas fa-trash fs-2"  style="color: '+Theme.gray +'"></i></a>'

          return render
        },
      },
    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("change", "#kt_files_datatables .check_btn", (e) => {
    e.stopImmediatePropagation()
    const target = e.target.closest('#kt_files_datatables .check_btn');
    let id = target.getAttribute('data-id')

    if (target.checked) {
      if (!files.value.includes(id)) {
        files.value.push(id);
      }
    } else {
      let index = files.value.indexOf(id);
        if (index !== -1) {
            files.value.splice(index, 1);
        }
    }

    let totalCheckboxes = $("#kt_files_datatables .check_btn").length;
    showAll.value = files.value.length === totalCheckboxes;
  });

  $(document).on("click", "#kt_files_datatables .download_btn", (e) => {
    e.stopImmediatePropagation()
    const target = e.target.closest('#kt_files_datatables .download_btn');
    downloadFile(target.getAttribute('data-id'), target.getAttribute('data-file'))
  });

  $(document).on("click", "#kt_files_datatables .view_btn", (e) => {

    const target = e.target.closest('#kt_files_datatables .view_btn');
    const fileName = target.getAttribute('data-file');
    const fileUrlAttr = target.getAttribute('data-file_url');
    const id = target.getAttribute('data-id');

    const extension = fileName ? fileName.split('.').pop().toLowerCase() : '';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension);
    const isPdf = extension === 'pdf';

    if (!isImage && !isPdf) {
      e.stopImmediatePropagation()
      downloadFile(id, fileName);
    } else {
      fileUrl.value = null;
      fileUrl.value = [{ file: fileName, file_url: fileUrlAttr }];
      $('#file_attach').modal('toggle');
    }


  });

  $(document).on("click", "#kt_files_datatables .delete_btn", (e) => {
    e.stopImmediatePropagation()
    const target = e.target.closest('#kt_files_datatables .delete_btn');
    let id = target.getAttribute('data-id')
    deleteFile(id)
  });

  $(document).on("click", "#kt_files_datatables .add_btn", (e) => {
    e.stopImmediatePropagation()
    const target = e.target.closest('#kt_files_datatables .add_btn');
    formUpdate.value.file_id = target.getAttribute('data-id');
    formUpdate.value.name = target.getAttribute('data-file');
    formUpdate.value.key_word = target.getAttribute('data-keywords');
    formUpdate.value.customer_can_view = target.getAttribute('data-can');

    $('#update_file_modal_create').modal('toggle')
  });

  $(document).on("change", "#kt_files_datatables .toggle-customer-view", function (event) {
    event.stopImmediatePropagation()
    const target = event.target.closest('#kt_files_datatables .toggle-customer-view');
    const data = {
      files: [target.getAttribute("data-id")],
      customer_can_view: target.checked
    }

    giveCustomerAccess(data);
  });
}

async function deleteDirectory(id){
  showSwal(
      t('projects.suppression'),
      t('projects.delete_directory')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('projects/deleteDirectory', {projectFolder: id})
          .then(() => {
            loadDirectories()
            window.toastr['success'](i18n.global.t('projects.project_folder_deleted_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

async function deleteFile(id){
  showSwal(
      t('projects.suppression'),
      t('projects.delete_file')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('projects/deleteDirectoryFile', {project_folder_file_id: id})
          .then(() => {
            fetchDatas()
            window.toastr['success'](i18n.global.t('projects.project_folder_file_deleted_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

async function downloadFile(id, name){
  await store.dispatch('projects/downloadProjectFile', {id, name})
    .then(() => {
      window.toastr['success'](i18n.global.t('projects.project_folder_file_download_successfully'))
    })
}



async function giveCustomerAccess(unique = {}){
  const data = unique.hasOwnProperty("files") ? unique : {
    files: files.value,
    customer_can_view: true
  }
  store.dispatch('projects/setCustomerCanViewFiles', data)
  .then((response) => {
    fetchDatas()
    window.toastr['success'](i18n.global.t('notification.access_updated_successfully'))
  })
  .catch(err => {
  })
}

async function downloadAllFiles(){
  await store.dispatch('projects/downloadProjectAllFile', {files: files.value})
    .then(() => {
      window.toastr['success'](i18n.global.t('projects.project_folder_file_download_successfully'))
    })
}

async function deleteAllFiles(){
  showSwal(
      t('projects.suppression'),
      t('projects.delete_all_file')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('projects/deleteDirectoryAllFile', {project_folder_id: activeDirectory.value, files: files.value})
          .then(() => {
            fetchDatas()
            showAll.value = false

            files.value = []
            files.value.length = 0
            window.toastr['success'](i18n.global.t('projects.project_folder_file_deleted_successfully'))
          })
      } catch (e) {
      }
    }
  });
}
</script>

<style scoped>
  .active {
    background-color: var(--bs-light-primary) !important;
    border: 1px solid var(--bs-light-primary) !important;
  }
</style>